/**
 * Ordering-block custom badges.
 * - Link targets use native anchors.
 * - Custom targets smooth-scroll with the sticky-header offset.
 * - Variant counts update for both the theme's native picker and Kaching.
 */
(function () {
  'use strict';

  if (window.__customBadgesInitialized) return;
  window.__customBadgesInitialized = true;

  var KACHING_PICKER_SELECTOR = 'select.kaching-bundles__bar-variant-select';
  var NATIVE_PICKER_SELECTOR =
    'variant-radios, variant-selects, secondary-variant-select, secondary-variant-select-separate';

  class CustomBadgesController {
    constructor() {
      this.kachingObserver = new MutationObserver((mutations) => {
        this.handleKachingMutations(mutations);
      });

      this.handleCustomTargetClick = this.handleCustomTargetClick.bind(this);
      this.handleVariantChange = this.handleVariantChange.bind(this);
    }

    initialize() {
      document.addEventListener('click', this.handleCustomTargetClick);
      document.addEventListener('change', this.handleVariantChange);

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.startKachingObserver(), {
          once: true,
        });
        return;
      }

      this.startKachingObserver();
    }

    startKachingObserver() {
      if (!document.body) return;

      this.kachingObserver.observe(document.body, {
        childList: true,
        subtree: true,
      });

      document.querySelectorAll(KACHING_PICKER_SELECTOR).forEach((picker) => {
        this.updateFromKachingPicker(picker);
      });
    }

    handleKachingMutations(mutations) {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof Element)) return;

          if (node.matches(KACHING_PICKER_SELECTOR)) {
            this.updateFromKachingPicker(node);
          }

          node.querySelectorAll(KACHING_PICKER_SELECTOR).forEach((picker) => {
            this.updateFromKachingPicker(picker);
          });
        });
      });
    }

    handleCustomTargetClick(event) {
      if (!(event.target instanceof Element)) return;

      var badgeAction = event.target.closest('[data-custom-badge-target]');
      if (!badgeAction) return;

      var customSelector = (badgeAction.dataset.customBadgeTarget || '').trim();
      if (!customSelector) return;

      var targetElement = this.findTargetElement(badgeAction, customSelector);
      if (!targetElement) {
        console.warn('[custom-badges] Custom target not found on page:', customSelector);
        return;
      }

      this.scrollToTarget(badgeAction, targetElement);
    }

    handleVariantChange(event) {
      var picker = event.target;
      if (!(picker instanceof Element)) return;

      if (picker.matches(KACHING_PICKER_SELECTOR)) {
        this.updateFromKachingPicker(picker);
        return;
      }

      var nativePicker = picker.closest(NATIVE_PICKER_SELECTOR);
      if (!nativePicker) return;

      window.setTimeout(() => {
        var context = this.getProductContext(nativePicker);
        var productInfo = nativePicker.closest('product-info');
        var variantInput = productInfo
          ? productInfo.querySelector('.product-variant-id, input[name="id"]')
          : null;

        if (!variantInput || !variantInput.value) return;
        this.updateCountByVariantId(context, variantInput.value);
      }, 0);
    }

    updateFromKachingPicker(picker) {
      var context = this.getProductContext(picker);
      var map = this.loadVariantCountMap(context);
      if (!map) return;

      var selectedOption =
        picker.options && picker.selectedIndex >= 0 ? picker.options[picker.selectedIndex] : null;
      var candidates = [
        picker.getAttribute('data-value'),
        picker.value,
        selectedOption ? selectedOption.getAttribute('data-value') : '',
        selectedOption ? selectedOption.value : '',
        selectedOption ? selectedOption.textContent : '',
      ].filter(Boolean);

      var matchedVariant = map.variants.find((variant) => {
        return candidates.some((candidate) => {
          var normalizedCandidate = this.normalizeValue(candidate);
          if (String(variant.id) === normalizedCandidate) return true;

          return variant.options.some((option) => {
            return this.normalizeValue(option) === normalizedCandidate;
          });
        });
      });

      if (matchedVariant) this.updateCount(context, matchedVariant.count);
    }

    updateCountByVariantId(context, variantId) {
      var map = this.loadVariantCountMap(context);
      if (!map) return;

      var matchedVariant = map.variants.find((variant) => {
        return String(variant.id) === String(variantId);
      });

      if (matchedVariant) this.updateCount(context, matchedVariant.count);
    }

    loadVariantCountMap(context) {
      var mapElement = context.querySelector('[data-custom-badge-variant-count-map]');
      if (!mapElement) return null;

      if (mapElement.__variantCountMap) return mapElement.__variantCountMap;

      try {
        var map = JSON.parse(mapElement.textContent);
        map.variants = Array.isArray(map.variants) ? map.variants : [];
        mapElement.__variantCountMap = map;
        return map;
      } catch (error) {
        console.warn('[custom-badges] Could not read the variant count map:', error);
        return null;
      }
    }

    updateCount(context, count) {
      var hasCount = count !== null && count !== undefined && String(count).trim() !== '';
      var countElements = context.querySelectorAll('[data-low-stock-variant-count]');
      var primaryLabels = context.querySelectorAll('[data-low-stock-count-label]');
      var fallbackLabels = context.querySelectorAll('[data-low-stock-count-fallback]');

      countElements.forEach((countElement) => {
        var valueElement = countElement.querySelector('[data-low-stock-variant-count-value]');
        if (!valueElement) return;

        countElement.hidden = !hasCount;
        valueElement.textContent = hasCount ? count : '';
        countElement.setAttribute('aria-label', hasCount ? 'Inventory count: ' + count : '');
      });

      primaryLabels.forEach((primaryLabel) => {
        primaryLabel.hidden = !hasCount;
      });

      fallbackLabels.forEach((fallbackLabel) => {
        fallbackLabel.hidden = hasCount;
      });
    }

    getProductContext(element) {
      return (
        element.closest('quick-add-modal') ||
        element.closest('section[id^="MainProduct-"]') ||
        element.closest('.section-featured-product') ||
        document
      );
    }

    normalizeValue(value) {
      return String(value).trim().toLowerCase();
    }

    findTargetElement(badgeAction, customSelector) {
      var quickAddModal = badgeAction.closest('quick-add-modal');
      var mainProduct =
        badgeAction.closest('section[id^="MainProduct-"]') ||
        badgeAction.closest('.section-featured-product');
      var scopes = quickAddModal ? [quickAddModal] : [mainProduct, document];

      try {
        for (var index = 0; index < scopes.length; index += 1) {
          var scope = scopes[index];
          if (!scope) continue;

          var targetElement = scope.querySelector(customSelector);
          if (targetElement) return targetElement;
        }
      } catch (error) {
        console.warn('[custom-badges] Invalid custom selector:', customSelector, error);
      }

      return null;
    }

    scrollToTarget(badgeAction, targetElement) {
      var quickAddModal = badgeAction.closest('quick-add-modal');
      var scrollBehavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ? 'auto'
        : 'smooth';

      if (quickAddModal) {
        var modalScroller = this.findModalScroller(quickAddModal);
        if (modalScroller) {
          var targetRect = targetElement.getBoundingClientRect();
          var scrollerRect = modalScroller.getBoundingClientRect();
          var modalPosition = targetRect.top - scrollerRect.top + modalScroller.scrollTop - 16;

          modalScroller.scrollTo({
            top: Math.max(0, modalPosition),
            behavior: scrollBehavior,
          });
          return;
        }
      }

      var headerOffset =
        parseInt(
          getComputedStyle(document.documentElement).getPropertyValue('--header-height'),
          10
        ) || 150;
      var pagePosition =
        targetElement.getBoundingClientRect().top + window.scrollY - headerOffset;

      window.scrollTo({
        top: Math.max(0, pagePosition),
        behavior: scrollBehavior,
      });
    }

    findModalScroller(quickAddModal) {
      var candidates = [
        quickAddModal.querySelector('.quick-add-modal__content-info'),
        quickAddModal.querySelector('.quick-add-modal__content'),
      ];

      for (var index = 0; index < candidates.length; index += 1) {
        var candidate = candidates[index];
        if (!candidate) continue;

        var overflowY = getComputedStyle(candidate).overflowY;
        var allowsScrolling = overflowY === 'auto' || overflowY === 'scroll';
        if (allowsScrolling && candidate.scrollHeight > candidate.clientHeight) {
          return candidate;
        }
      }

      return candidates[1] || candidates[0] || null;
    }
  }

  var customBadgesController = new CustomBadgesController();
  customBadgesController.initialize();
})();
